import { z } from "zod";

/**
 * The `/api/listings` contract.
 *
 * It's expressed as zod schemas rather than bare interfaces because this is a
 * trust boundary: the server builds these shapes from a database it doesn't
 * own the writes to, and the UI parses whatever comes back off the wire. A
 * schema catches contract drift at the edge instead of as an undefined three
 * components deep.
 */

/**
 * One leg of a multi-modal transit journey — "13 min on the 38R". The `housing`
 * CLI merges TravelTime's fine-grained parts down to these before storing them.
 */
export type TravelLeg = z.infer<typeof TravelLegSchema>;
export const TravelLegSchema = z.object({
  /** "walk" | "bus" | "train" | "rail_underground" | "ferry" | … — open-ended by source. */
  mode: z.string(),
  mins: z.number(),
  /** Short line code where the source gave one, e.g. "38R". */
  line: z.string().optional(),
});

/**
 * Door-to-door times from this unit to the office anchor, in minutes.
 *
 * Every mode is nullable and independently so: the ingest prices each one
 * separately and stores NULL when the trip exceeds that mode's ceiling, which
 * reads as "not an option this way" rather than "unknown". A unit 40km out has
 * a `transit` and a `drive` but no `walk`.
 *
 * There is no scooter field — it's derived from `drive`, so storing it would be
 * a second copy of the same fact that could drift from the multiplier.
 */
export type Travel = z.infer<typeof TravelSchema>;
export const TravelSchema = z.object({
  walk: z.number().nullable(),
  drive: z.number().nullable(),
  transit: z.number().nullable(),
  /**
   * The per-leg transit itinerary, when one was fetched (only close-in units
   * get one — the CLI gates it behind a transit-minute cutoff). Empty means no
   * breakdown was stored, not a zero-leg journey.
   */
  legs: z.array(TravelLegSchema),
});

/** One rental unit as plotted on the map. */
export type Listing = z.infer<typeof ListingSchema>;
export const ListingSchema = z.object({
  /** "<source>:<sourceId>" — stable across runs. */
  id: z.string(),
  source: z.string(),
  url: z.string(),
  title: z.string().nullable(),
  address: z.string().nullable(),
  neighborhood: z.string().nullable(),
  lat: z.number(),
  lon: z.number(),
  /**
   * Monthly rent in USD. Always present: units where no source published an
   * asking rent are dropped server-side, since they can't be filtered on or
   * labelled on the map.
   */
  price: z.number(),
  /** 0 = studio. Null when the source never said. */
  beds: z.number().nullable(),
  baths: z.number().nullable(),
  sqft: z.number().nullable(),
  propertyType: z.string().nullable(),
  /** Epoch ms the listing went up (posted_at, else when we first saw it). */
  postedAt: z.number(),
  /** True when postedAt is a first_seen fallback rather than a real post date. */
  postedApprox: z.boolean(),
  /** Photo URLs pulled out of the source payload; may be empty. */
  photos: z.array(z.string()),
  /** Other sources carrying this same unit, deduped away from the map. */
  alsoOn: z.array(z.object({ source: z.string(), url: z.string() })),
  /** Times to the office anchor. Every mode may be null — see TravelSchema. */
  travel: TravelSchema,
});

/**
 * Where every `travel` time is measured to. Null when the server has no
 * HOUSING_ANCHOR configured — the times are still correct (the ingest computed
 * them against a real anchor), we just can't say on screen where "there" is.
 */
export type Anchor = z.infer<typeof AnchorSchema>;
export const AnchorSchema = z.object({
  lat: z.number(),
  lon: z.number(),
  /** Human name for the destination, when HOUSING_ANCHOR_LABEL is set. */
  label: z.string().nullable(),
});

export type ListingsResponse = z.infer<typeof ListingsResponseSchema>;
export const ListingsResponseSchema = z.object({
  listings: z.array(ListingSchema),
  /** The destination every listing's `travel` is measured to, if known. */
  anchor: AnchorSchema.nullable(),
  /** Epoch ms the server built this snapshot. */
  generatedAt: z.number(),
  /** Freshest last_seen across the DB — i.e. when ingest last ran. */
  lastIngestAt: z.number().nullable(),
  /** Rows folded away as duplicate units, for the footer stat. */
  deduped: z.number(),
  /** Units dropped because no source published an asking rent. */
  unpriced: z.number(),
  /**
   * Units dropped for having fewer baths than bedrooms (a 2BD/1BA) — a hard
   * disqualifier, not a filter. Defaulted so a snapshot cached at the edge
   * before this field existed still parses during the few minutes it lives on.
   */
  underBathed: z.number().default(0),
});
